import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/marketing/MarketingNav";
import { AurumOrb } from "@/components/brand/AurumOrb";
import { Waitlist } from "@/components/marketing/Waitlist";
import { StatusBadge, Pill } from "@/components/common";
import {
  ArrowRight, ShieldCheck, Activity, Scale, Bell, BookOpen,
  Brain, Check, Clock, TrendingUp, Newspaper
} from "lucide-react";

const PIPELINE = [
  "Live Data", "Regime", "HTF Context", "Key Levels", "Strategy", "Setup",
  "Confirmation", "News Filter", "Devil's Advocate", "Setup Score", "Risk", "Trade Plan",
];

const FEATURES = [
  { icon: Activity, title: "Market Regime Engine", desc: "Deterministic classification — trend, range, compression, breakout, expansion — from real structure, EMAs, ADX and ATR. Never a guess." },
  { icon: TrendingUp, title: "Multi-Timeframe Analysis", desc: "4H bias, 1H structure, 15M setup, 5M trigger. Low-timeframe signals never override strong higher-timeframe context without warning." },
  { icon: Scale, title: "Risk & Position Sizing", desc: "Exact XAU/USD lot sizing from your balance, risk % and structural stop. The AI never invents a single number." },
  { icon: Brain, title: "Devil's Advocate", desc: "Before validating any trade, Aurum actively argues against it — overextension, chasing, liquidity sweeps, weak R:R." },
  { icon: Bell, title: "Entry Alerts", desc: "SETUP FORMING → WAITING FOR CONFIRMATION → ENTRY CONDITIONS SATISFIED. And ENTRY MISSED — DO NOT CHASE." },
  { icon: BookOpen, title: "Journal & AI Coach", desc: "Every paper trade is journaled automatically, then analyzed for patterns — without fabricating significance on small samples." },
];

const STORY = [
  { label: "Trend", value: "✓ Aligned" }, { label: "Market Structure", value: "✓ Higher low" },
  { label: "Key Location", value: "✓ At support" }, { label: "News", value: "✓ Clear" },
  { label: "Risk", value: "✓ Within plan" }, { label: "Confirmation", value: "Pending" },
];

export default function Landing() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)] min-h-screen">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 75% 15%, rgba(214,174,87,0.12), transparent 45%), radial-gradient(circle at 10% 80%, rgba(96,165,250,0.06), transparent 40%)" }} />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <Pill color="#D6AE57"><span className="animate-pulse">●</span> AI Gold Trading Copilot · XAU/USD</Pill>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Stop watching<br />Gold all day.
            </h1>
            <p className="mt-6 text-lg text-[var(--text-sec)] max-w-lg">
              Aurum monitors XAU/USD against your trading plan, validates setups, calculates
              risk, and alerts you when your conditions appear — and tells you to wait when they don't.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-cta-primary" className="btn-gold flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all hover:brightness-110">
                Start Paper Trading <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-cta-secondary" className="flex items-center gap-2 rounded-full border border-[var(--border2)] px-6 py-3 text-sm font-medium hover:border-aurum hover:text-aurum transition-colors">
                Watch Aurum Analyze a Trade
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-5 text-xs text-[var(--text-muted)]">
              <span className="flex items-center gap-1.5"><Check size={13} className="text-bull" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Check size={13} className="text-bull" /> Free during launch</span>
              <span className="flex items-center gap-1.5"><Check size={13} className="text-bull" /> Discipline-first</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <TerminalPreview />
          </motion.div>
        </div>

        {/* pipeline ticker */}
        <div className="border-y border-[var(--border2)] bg-[var(--bg-elev)] overflow-hidden">
          <div className="flex gap-3 py-3 animate-ticker whitespace-nowrap" style={{ width: "max-content" }}>
            {[...PIPELINE, ...PIPELINE].map((p, i) => (
              <span key={i} className="flex items-center gap-3 text-xs text-[var(--text-muted)] px-3">
                {p} <span className="text-aurum">→</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-wider text-aurum font-medium">Product philosophy</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight">A good assistant does not create more trades.</h2>
          <p className="mt-4 text-[var(--text-sec)]">
            Aurum isn't built to predict the future. It's built to continuously watch the market,
            apply your rules, identify qualifying conditions, calculate the risk, challenge the
            setup, and enforce discipline. It frequently says <span className="text-[var(--text)] font-medium">NO TRADE</span> — because avoiding poor trades is part of the value.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-xl border border-[var(--border2)] bg-[var(--card2)] p-6 hover:border-aurum transition-colors">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(214,174,87,0.12)" }}>
                <f.icon size={20} className="text-aurum" />
              </div>
              <h3 className="mt-4 font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-[var(--text-sec)]">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STORY: watches while you work */}
      <section className="border-y border-[var(--border2)] bg-[var(--bg-elev)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs uppercase tracking-wider text-aurum font-medium">While you work</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight">Aurum watches. A setup begins forming. Aurum validates it.</h2>
            <p className="mt-4 text-[var(--text-sec)]">
              You get on with your day. Aurum tracks structure, location, news and confirmation —
              and only surfaces a trade plan when your criteria are genuinely met.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {STORY.map((s) => (
                <div key={s.label} className="flex items-center justify-between rounded-lg border border-[var(--border2)] bg-[var(--card2)] px-4 py-3">
                  <span className="text-sm text-[var(--text-sec)]">{s.label}</span>
                  <span className={`text-sm font-medium ${s.value.startsWith("✓") ? "text-bull" : "text-warn"}`}>{s.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-6"><StatusBadge status="WAITING FOR CONFIRMATION" /></div>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[var(--border2)]">
            <img src="https://images.unsplash.com/photo-1614028674026-a65e31bfd27c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Trader monitoring markets" className="w-full h-full object-cover" style={{ maxHeight: 460 }} loading="lazy" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,9,12,0.1), rgba(7,9,12,0.75))" }} />
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
              <AurumOrb status="SETUP FORMING" size={56} />
              <div className="glass rounded-xl px-4 py-2 border border-[var(--border2)]">
                <div className="text-xs text-[var(--text-muted)]">Current action</div>
                <div className="text-sm font-semibold">SETUP FORMING · watching support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 text-center">
        <AurumOrb status="ENTRY CONDITIONS SATISFIED" size={80} />
        <h2 className="mt-6 text-3xl lg:text-5xl font-bold tracking-tight">Intelligence before execution.</h2>
        <p className="mt-4 text-[var(--text-sec)] max-w-xl mx-auto">
          Build your plan in minutes. Let Aurum monitor Gold against it — calmly, continuously, and without emotion.
        </p>
        <Link to="/signup" className="mt-8 inline-flex items-center gap-2 btn-gold rounded-full px-7 py-3.5 text-sm font-semibold hover:brightness-110 transition-all">
          Start Paper Trading Free <ArrowRight size={16} />
        </Link>

        <div className="mt-16 pt-12 border-t border-[var(--border2)] max-w-xl mx-auto">
          <span className="text-xs uppercase tracking-wider text-aurum font-medium">Early access</span>
          <h3 className="mt-3 text-2xl font-bold tracking-tight">Be first when live spots open</h3>
          <p className="mt-3 text-sm text-[var(--text-sec)]">
            Join the waitlist to get priority access, launch pricing, and product updates as Aurum rolls out live trading intelligence.
          </p>
          <Waitlist />
        </div>

        <p className="mt-10 text-xs text-[var(--text-muted)]">Trading involves substantial risk of loss. Not investment advice.</p>
      </section>

      <Footer />
    </div>
  );
}

function TerminalPreview() {
  return (
    <div className="rounded-2xl border border-[var(--border2)] bg-[var(--card2)] overflow-hidden shadow-2xl" style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6)" }}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border2)] bg-[var(--bg-elev)]">
        <span className="h-2.5 w-2.5 rounded-full bg-bear/70" /><span className="h-2.5 w-2.5 rounded-full bg-warn/70" /><span className="h-2.5 w-2.5 rounded-full bg-bull/70" />
        <span className="ml-3 text-xs text-[var(--text-muted)]">Aurum Terminal · XAU/USD</span>
        <span className="ml-auto"><Pill color="#F5B942">DEMO</Pill></span>
      </div>
      <div className="p-4 grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div><div className="text-xs text-[var(--text-muted)]">XAU/USD</div><div className="tnum text-2xl font-bold">$2,417.60</div></div>
            <div className="text-right"><div className="tnum text-bull text-sm">+18.40 (0.77%)</div><div className="text-[10px] text-[var(--text-muted)]">London · overlap</div></div>
          </div>
          <div className="h-28 rounded-lg relative overflow-hidden" style={{ background: "linear-gradient(180deg,#0D1117,#121821)" }}>
            <svg viewBox="0 0 300 100" className="w-full h-full">
              {[...Array(28)].map((_, i) => {
                const up = Math.sin(i * 0.9) > -0.2; const x = 8 + i * 10;
                const h = 14 + Math.abs(Math.sin(i * 1.3)) * 30; const y = 55 - Math.sin(i * 0.5) * 18;
                return <g key={i}><line x1={x} x2={x} y1={y - 8} y2={y + h + 6} stroke={up ? "#21C58E" : "#EF6262"} strokeWidth="1" /><rect x={x - 2.5} y={y} width="5" height={h} fill={up ? "#21C58E" : "#EF6262"} /></g>;
              })}
              <line x1="0" x2="300" y1="40" y2="40" stroke="#D6AE57" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            </svg>
          </div>
          <div className="rounded-lg border border-[var(--border2)] p-3">
            <div className="flex items-center justify-between text-xs"><span className="text-[var(--text-muted)]">Trade Plan · Trend Pullback</span><Pill color="#21C58E">LONG</Pill></div>
            <div className="mt-2 grid grid-cols-4 gap-2 tnum text-[11px]">
              <div><div className="text-[var(--text-muted)]">Entry</div><div className="font-semibold">2412.0</div></div>
              <div><div className="text-[var(--text-muted)]">Stop</div><div className="text-bear font-semibold">2402.5</div></div>
              <div><div className="text-[var(--text-muted)]">TP1</div><div className="text-bull font-semibold">2431.0</div></div>
              <div><div className="text-[var(--text-muted)]">Size</div><div className="text-aurum font-semibold">0.05</div></div>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border border-[var(--border2)] p-3 flex flex-col items-center">
            <AurumOrb status="ENTRY CONDITIONS SATISFIED" size={54} />
            <div className="mt-2 text-[10px] text-center text-bull font-semibold leading-tight">ENTRY CONDITIONS SATISFIED</div>
          </div>
          <div className="rounded-lg border border-[var(--border2)] p-3 text-center">
            <div className="text-[10px] text-[var(--text-muted)]">Setup Quality</div>
            <div className="tnum text-2xl font-bold text-bull">84</div>
            <div className="text-[10px] text-[var(--text-muted)]">HIGH QUALITY</div>
          </div>
          <div className="rounded-lg border border-[var(--border2)] p-3 flex items-center gap-2">
            <Newspaper size={13} className="text-bull" /><div><div className="text-[10px] text-[var(--text-muted)]">News</div><div className="text-xs font-semibold text-bull">LOW</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
